/**
 * SEO Configuration
 * Centralized per-route SEO data and structured data builders.
 */

import type { SEOConfig } from '@hooks/useSEO';
import { OG_IMAGE, TWITTER_IMAGE, WEB_URL } from '@parlay-picker/shared/appConfig';
import type { FaqCategory } from '@parlay-picker/shared/faqData';
import { TIER_PICKS_QUOTAS } from '@parlay-picker/shared/subscriptionTier';

const SITE_URL = WEB_URL;

// =============================================================================
// Static Route Configs
// =============================================================================

export const SEO_LANDING: SEOConfig = {
  title: 'AI Parlay Picker - Smart Basketball Betting Analysis & Tracking',
  description: `Generate winning basketball parlays with AI-powered picks. Track bets in real-time, analyze performance, and find the best odds across sportsbooks. Free tier includes ${TIER_PICKS_QUOTAS.free.standard} picks daily.`,
  canonical: `${SITE_URL}/`,
  ogTitle: 'AI Parlay Picker - Smart Basketball Betting Analysis',
  ogDescription:
    'Generate winning basketball parlays with AI-powered picks. Track bets in real-time and analyze your performance.',
  ogImage: OG_IMAGE,
  twitterTitle: 'AI Parlay Picker - Smart Basketball Betting',
  twitterDescription:
    'Generate winning basketball parlays with AI-powered picks and real-time tracking.',
  twitterImage: TWITTER_IMAGE,
};

export const SEO_LOGIN: SEOConfig = {
  title: 'Sign In - Parlay Picker',
  description:
    'Sign in to your Parlay Picker account to access AI-powered basketball picks, track parlays, and analyze your betting performance.',
  canonical: `${SITE_URL}/login`,
  noindex: true,
  ogImage: OG_IMAGE,
  twitterImage: TWITTER_IMAGE,
};

export const SEO_REGISTER: SEOConfig = {
  title: 'Create Account - Parlay Picker',
  description:
    'Join Parlay Picker for free. Get 2 AI-generated basketball picks daily, track parlays in real-time, and analyze your betting performance. No credit card required.',
  canonical: `${SITE_URL}/register`,
  ogTitle: 'Join Parlay Picker - Free AI Basketball Picks',
  ogDescription:
    'Get 2 AI-generated basketball picks daily for free. Track parlays, analyze performance, and find the best odds.',
  ogImage: OG_IMAGE,
  twitterImage: TWITTER_IMAGE,
};

export const SEO_FAQ: SEOConfig = {
  title: 'FAQ & Guides - Parlay Picker',
  description:
    'Everything you need to know about Parlay Picker: how AI picks work, risk tiers, scoring breakdowns, line shopping, tracking, settlement, and pricing.',
  canonical: `${SITE_URL}/faq`,
  ogTitle: 'FAQ & Guides - Parlay Picker',
  ogDescription:
    'Learn how AI-powered basketball pick generation, scoring, and tracking work. Complete guides on risk tiers, line shopping, and betting strategy.',
  ogImage: OG_IMAGE,
  twitterImage: TWITTER_IMAGE,
};

export const SEO_CONTACT: SEOConfig = {
  title: 'Contact Us - Parlay Picker',
  description:
    'Get in touch with the Parlay Picker team. Report issues, request features, ask questions, or share feedback. We typically respond within 24-48 hours.',
  canonical: `${SITE_URL}/contact`,
  ogTitle: 'Contact Parlay Picker',
  ogDescription:
    'Have questions or feedback? Contact the Parlay Picker team. We typically respond within 24-48 hours.',
  ogImage: OG_IMAGE,
  twitterImage: TWITTER_IMAGE,
};

// =============================================================================
// FAQ JSON-LD Builder
// =============================================================================

interface FaqJsonLdQuestion {
  '@type': 'Question';
  name: string;
  acceptedAnswer: {
    '@type': 'Answer';
    text: string;
  };
}

interface FaqJsonLd {
  '@context': 'https://schema.org';
  '@type': 'FAQPage';
  mainEntity: FaqJsonLdQuestion[];
}

export function buildFaqJsonLd(categories: FaqCategory[]): FaqJsonLd {
  const questions: FaqJsonLdQuestion[] = categories.flatMap((category) =>
    category.items.map((item) => ({
      '@type': 'Question' as const,
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer' as const,
        text: item.answer,
      },
    })),
  );

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions,
  };
}
